import {
  getShopifyEmbeddedContext,
  isEmbeddedShopifyLaunch,
  openShopifyTopLevel,
} from "@/lib/shopifyEmbedded";

const AUTO_RECONNECT_KEY_PREFIX = "shopify_auto_reconnect";
const AUTO_RECONNECT_RETRY_WINDOW_MS = 30 * 1000;

function normalizeShopDomain(value?: string | null): string {
  return (value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "");
}

function buildReconnectKey(merchantId?: string | null, shop?: string | null): string | null {
  if (!merchantId) {
    return null;
  }

  const normalizedShop = normalizeShopDomain(shop);
  return `${AUTO_RECONNECT_KEY_PREFIX}:${merchantId}:${normalizedShop || "unknown"}`;
}

export function clearShopifyAutoReconnectAttempt(
  merchantId?: string | null,
  shop?: string | null,
): void {
  if (typeof window === "undefined") {
    return;
  }

  const key = buildReconnectKey(merchantId, shop);
  if (!key) {
    return;
  }

  sessionStorage.removeItem(key);
}

export function maybeStartShopifyAutoReconnect(options: {
  hasAccessToken?: boolean | null;
  merchantId?: string | null;
  merchantShopDomain?: string | null;
  searchParams: URLSearchParams;
}): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const { hasAccessToken, merchantId, merchantShopDomain, searchParams } = options;
  if (!merchantId || hasAccessToken) {
    return false;
  }

  if (!isEmbeddedShopifyLaunch(searchParams)) {
    return false;
  }

  const embeddedContext = getShopifyEmbeddedContext(searchParams);
  const embeddedShop = normalizeShopDomain(embeddedContext.shop);
  const merchantShop = normalizeShopDomain(merchantShopDomain);

  if (!embeddedShop) {
    return false;
  }

  // Avoid reconnecting the wrong merchant when the linked store and embedded shop don't match.
  if (merchantShop && merchantShop !== embeddedShop) {
    return false;
  }

  const reconnectKey = buildReconnectKey(merchantId, embeddedShop);
  if (!reconnectKey) {
    return false;
  }

  const previousAttemptValue = sessionStorage.getItem(reconnectKey);
  if (previousAttemptValue) {
    const previousAttemptAt = Number.parseInt(previousAttemptValue, 10);
    const hasRecentAttempt =
      Number.isFinite(previousAttemptAt) &&
      previousAttemptAt > 0 &&
      Date.now() - previousAttemptAt < AUTO_RECONNECT_RETRY_WINDOW_MS;

    if (hasRecentAttempt) {
      return false;
    }
  }

  sessionStorage.setItem(reconnectKey, String(Date.now()));

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const oauthInitUrl = new URL(`${supabaseUrl}/functions/v1/shopify-oauth-init`);
  oauthInitUrl.searchParams.set("shop", embeddedShop);
  oauthInitUrl.searchParams.set("merchant_id", merchantId);

  if (embeddedContext.host) {
    oauthInitUrl.searchParams.set("host", embeddedContext.host);
  }

  openShopifyTopLevel(oauthInitUrl.toString());
  return true;
}
